import { v4 as uuidv4 } from 'uuid';
import type { Chord } from '@/types/chord';
import type { Move, MoveContext, MoveResult } from '../types';

/**
 * suspend into it — 4-3 suspension. Splits the selected chord in half:
 * first half is the same chord with a sus4 added, second half is the
 * original resolution. The resolution keeps the original chord's id (it's
 * the same chord, just shorter); the suspension half gets a new id.
 */
export const suspendIntoIt: Move = {
  id: 'suspend-into-it',
  name: 'suspend into it',
  theory: '4–3 suspension',

  applicable(ctx: MoveContext): boolean {
    const chord = ctx.chords[ctx.index];
    if (!chord) return false;
    if (chord.duration < 1) return false;
    return chord.quality === 'major' || chord.quality === 'minor';
  },

  apply(ctx: MoveContext): MoveResult {
    const chord = ctx.chords[ctx.index];
    const firstDuration = chord.duration / 2;
    const secondDuration = chord.duration - firstDuration;

    const suspension: Chord = {
      ...chord,
      id: uuidv4(),
      startBeat: chord.startBeat,
      duration: firstDuration,
      extensions: { ...chord.extensions, sus4: true },
      key: ctx.key,
      mode: ctx.mode,
    };
    const resolution: Chord = {
      ...chord,
      startBeat: chord.startBeat + firstDuration,
      duration: secondDuration,
      key: ctx.key,
      mode: ctx.mode,
    };

    const chords: Chord[] = [
      ...ctx.chords.slice(0, ctx.index),
      suspension,
      resolution,
      ...ctx.chords.slice(ctx.index + 1),
    ];

    return {
      chords,
      changedIds: [suspension.id, resolution.id],
      explanation: 'held the fourth a moment longer than the ear expects, then let it fall.',
      theoryLabel: '4–3 suspension',
    };
  },
};
